import * as React from 'react';
import { cn } from 'utils/cn';
import { Drawer as DrawerPrimitive } from 'vaul';

const DrawerRoot = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);
DrawerRoot.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('backdrop-blur-md  fixed inset-0 z-10 bg-black/40', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    direction?: 'top' | 'bottom' | 'left' | 'right';
    container?: HTMLElement | ShadowRoot | null;
    blur?: boolean;
  }
>(({ className, blur = true, children, direction = 'bottom', container = document.body, ...props }, ref) => (
  <DrawerPortal container={container}>
    <DrawerOverlay className={`${blur ? "backdrop-blur-sm" : "backdrop-blur-none"}`} />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        'fixed isolate z-10 flex border bg-background border-border select-text',
        {
          'inset-x-0 bottom-0 mt-24 rounded-t-[10px]': direction === 'bottom',
          'inset-x-0 top-0 mb-24 rounded-b-[10px]': direction === 'top',
          'inset-y-0 right-0 ml-24 rounded-l-[10px]': direction === 'right',
          'inset-y-0 left-0 mr-24 rounded-r-[10px]': direction === 'left',
        },
        direction === 'left' || direction === 'right'
          ? 'h-full w-full max-w-[350px] flex-row'
          : 'h-auto w-full flex-col',
        className
      )}
      {...props}
    >
      <div
        className={cn('bg-muted dark:bg-card rounded-full', {
          'mx-auto mb-2 h-2 w-[100px]': direction === 'bottom',
          'mx-auto mb-4 h-2 w-[100px]': direction === 'top',
          'mb-auto ml-4 mt-auto h-[100px] w-2': direction === 'right',
          'mb-auto mr-4 mt-auto h-[100px] w-2': direction === 'left',
        })}
      />
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight text-textcolor',
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

const Drawer = Object.assign(DrawerRoot, {
  Portal: DrawerPortal,
  Overlay: DrawerOverlay,
  Trigger: DrawerTrigger,
  Close: DrawerClose,
  Content: DrawerContent,
  Header: DrawerHeader,
  Footer: DrawerFooter,
  Title: DrawerTitle,
  Description: DrawerDescription,
});

export default Drawer;
